import React from "react";
import { Layout, Row, Col, Typography, Button, Image } from "antd";
import {
  CalendarOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import mainEventImg from "../../assets/bloom-main.jpg";
import heroesCupImg from "../../assets/heroes-cup.jpg";
import openEventsImg from "../../assets/open-event.jpg";
import gallery1 from "../../assets/gallery1.jpg";
import gallery2 from "../../assets/gallery2.jpg";
import gallery3 from "../../assets/gallery3.jpg";

const { Title, Paragraph, Text } = Typography;
const { Content } = Layout;

// Helper function to check for mobile size
const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

const EventItem = ({ title, description, image, path, isLast }) => {
  const navigate = useNavigate();

  // Determine Col spans: 24 for mobile, 15/7 for desktop
  const imageColSpan = isMobile ? 10 : 15;
  const textColSpan = isMobile ? 14 : 7;

  // Desktop only styles
  const desktopStyles = isMobile
    ? {}
    : {
        marginLeft: "-40px", // The desktop overlap effect
      };

  // Mobile only styles to ensure text aligns after image
  const mobileTextStyles = isMobile ? { padding: "0 0 0 12px" } : {};

  // Mobile only styles for alignment (no fixed padding/margin on mobile)
  const mobileContainerStyles = isMobile
    ? {
        border: "1px solid #f0f0f0",
        borderRadius: "8px",
        padding: "12px",
        minHeight: "120px",
      }
    : {
        marginBottom: isLast ? "0" : "-70px",
        minHeight: "140px",
      };

  // Mobile text margin fix
  const mobileParagraphMargin = isMobile ? "16px" : isLast ? "120px" : "70px";

  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        overflow: "hidden",
        ...mobileContainerStyles,
      }}
    >
      <Row style={{ width: "100%", height: "100%" }}>
        {/* Image Column */}
        <Col
          span={imageColSpan}
          style={{
            height: "100%",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src={image}
            alt={title}
            style={{
              width: isMobile ? "90%" : "70%",
              height: isMobile ? "90%" : "70%",
              objectFit: "cover",
              borderRadius: isMobile ? "4px" : "0px",
            }}
          />
        </Col>

        {/* Text Column - Crucial fix applied here */}
        <Col
          span={textColSpan}
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            ...desktopStyles, // Apply negative margin ONLY on desktop
            ...mobileTextStyles,
          }}
        >
          <Title
            level={5}
            style={{
              marginBottom: isMobile ? "4px" : "10px",
              fontSize: isMobile ? "12px" : "14px",
              lineHeight: "1.4",
              // Fix: Ensure text aligns left on mobile inside the Col
              textAlign: "left",
            }}
          >
            {title}
          </Title>
          <Paragraph
            style={{
              marginBottom: mobileParagraphMargin, // Adjusted based on desktop need vs mobile flow
              fontSize: isMobile ? "11px" : "16px",
              lineHeight: "1.3",
              color: "#666",
              // Fix: Ensure text aligns left on mobile inside the Col
              textAlign: "left",
              // Add padding to prevent text from touching the edge of the mobile column
              paddingRight: isMobile ? "8px" : "0",
            }}
          >
            {description}
          </Paragraph>
          <Button
            type="link"
            onClick={() => navigate(path)}
            style={{
              padding: 0,
              alignSelf: "flex-start",
              fontSize: isMobile ? "12px" : "14px",
              height: "auto",
              fontWeight: "500",
            }}
          >
            Read More
          </Button>
        </Col>
      </Row>
    </div>
  );
};

const BloomDayPage = () => {

  // Re-declare isMobile inside the component or use window.innerWidth check in logic
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  return (
    <Layout>
      <Content
        style={{
          padding: isMobile ? "20px 16px" : "40px 80px",
          background: "#fff",
        }}
      >
        {/* Top Section */}
        <Row
          gutter={[isMobile ? 16 : 2, isMobile ? 16 : 2]}
          align="stretch"
          style={{ minHeight: isMobile ? "auto" : "450px" }}
        >
          {/* Left Large Image */}
          <Col xs={24} lg={14}>
            <div
              style={{
                height: isMobile ? "300px" : "100%",
                overflow: "hidden",
                marginBottom: isMobile ? "20px" : "0",
              }}
            >
              <Image
                src={mainEventImg}
                alt="Bloom Day"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
                preview={false}
              />
            </div>
          </Col>

          {/* Right Side Events (Refactored to use EventItem component) */}
          <Col xs={24} lg={10}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                height: isMobile ? "auto" : "100%",
                gap: isMobile ? "20px" : "0",
              }}
            >
              <EventItem
                title="Heroes Cup"
                description="Celebrating our laborers & company teams together."
                image={heroesCupImg}
                path="/heroes-day"
                isLast={false}
              />
              <EventItem
                title="Open Events"
                description="Anyone can join and enjoy the passion."
                image={openEventsImg}
                path="/open-events"
                isLast={true}
              />
            </div>
          </Col>
        </Row>

        {/* Event Info Section */}
        <div style={{ marginTop: isMobile ? "30px" : "50px" }}>
          <Title
            level={3}
            style={{
              color: "#722ed1",
              fontSize: isMobile ? "20px" : "28px",
              textAlign: isMobile ? "center" : "left",
              lineHeight: "1.3",
            }}
          >
            BloomDay: Empowering Children with Disabilities and Celebrating
            Families Through Sport
          </Title>

          <Title
            level={4}
            style={{
              fontSize: isMobile ? "16px" : "20px",
              marginTop: isMobile ? "20px" : "24px",
            }}
          >
            Event Description
          </Title>
          <Paragraph
            style={{
              maxWidth: "900px",
              fontSize: isMobile ? "14px" : "16px",
              lineHeight: "1.6",
              textAlign: isMobile ? "justify" : "left",
            }}
          >
            Join us for BloomDay, a joyful outdoor gathering designed especially
            for children aged 8 and up with physical or intellectual challenges,
            together with their parents or caregivers. It's a day filled with
            fun-adapted activities, tailored equipment and a caring atmosphere
            where every child is encouraged, supported and celebrated. Together
            we'll explore inclusive games, sensory-friendly play zones,
            parent-child teamwork challenges and plenty of moments that build
            confidence, connection and growth through play.
          </Paragraph>

          <Title
            level={4}
            style={{
              fontSize: isMobile ? "16px" : "20px",
              marginTop: isMobile ? "20px" : "24px",
            }}
          >
            Event Date and Location
          </Title>
          <Paragraph
            style={{
              fontSize: isMobile ? "14px" : "16px",
              lineHeight: "1.6",
            }}
          >
            <Text strong>
              <CalendarOutlined /> Coming Soon
            </Text>{" "}
          </Paragraph>
        </div>

        {/* Gallery Section */}
        <div style={{ marginTop: isMobile ? "30px" : "50px" }}>
          <Title
            level={4}
            style={{
              fontSize: isMobile ? "16px" : "20px",
              textAlign: isMobile ? "center" : "left",
            }}
          >
            Gallery
          </Title>
          <Row gutter={[isMobile ? 8 : 4, isMobile ? 8 : 4]}>
            {[gallery1, gallery2, gallery3].map((img, index) => (
              <Col xs={24} sm={8} key={index}>
                <div
                  style={{
                    width: "90%",
                    height: isMobile ? "200px" : "250px",
                    overflow: "hidden",
                    borderRadius: "10px",
                    margin: "0 auto",
                  }}
                >
                  <Image
                    src={img}
                    alt={`Gallery ${index + 1}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: "10px",
                    }}
                    preview={false}
                  />
                </div>
              </Col>
            ))}
          </Row>
        </div>

        {/* Price + Button */}
        <div
          style={{
            marginTop: isMobile ? "30px" : "40px",
            fontSize: isMobile ? "16px" : "20px",
            lineHeight: "1.6",
            textAlign: isMobile ? "center" : "left",
          }}
        >
          <Paragraph>
            <Text strong>Price:</Text> To be announced
          </Paragraph>
          <Paragraph>
            <Text strong>Ready to join us?</Text>
          </Paragraph>
          <Paragraph>
            Tap below to message our team on WhatsApp and reserve your spot.
          </Paragraph>

          <Button
            type="primary"
            icon={<WhatsAppOutlined style={{ color: "#25D366" }} />}
            style={{
              backgroundColor: "#480558",
              borderColor: "#480558",
              borderRadius: "8px",
              fontWeight: 600,
              width: isMobile ? "100%" : "auto",
              height: isMobile ? "48px" : "auto",
              fontSize: isMobile ? "16px" : "14px",
              marginTop: isMobile ? "10px" : "0",
              padding: isMobile ? "10px 16px" : "10px 24px",
            }}
          >
            Connect On WhatsApp
          </Button>
        </div>
      </Content>
    </Layout>
  );
};

export default BloomDayPage;
